import React, { useEffect, useState } from 'react';
import { useUserRole } from '../../contexts/UserRoleContext';
import { supabaseClient } from '../../services/supabase';
import { UserProfile, UserRole } from '../../types/user';
import './UserManagementPage.css';

interface AuthUser {
  id: string;
  email?: string;
  full_name?: string;
  role?: string;
  created_at?: string;
  updated_at?: string;
  last_sign_in_at?: string;
  email_confirmed_at?: string;
  created_at_auth?: string;
}

interface RoleRequest {
  id: string;
  user_id: string;
  requester_email: string;
  requester_name: string;
  requested_role: string;
  requested_location: string;
  requested_location_type: string;
  status: string;
  created_at: string;
}

const UserManagementPage: React.FC = () => {
  const { hasRole } = useUserRole();
  const [authUsers, setAuthUsers] = useState<AuthUser[]>([]);
  const [userProfiles, setUserProfiles] = useState<UserProfile[]>([]);
  const [roleRequests, setRoleRequests] = useState<RoleRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
    fetchRoleRequests();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      // Call the function to get user management data with last sign-in info
      const { data: users, error } = await supabaseClient
        .rpc('get_user_management_data');
      
      if (error) {
        console.error('Error fetching user management data:', error);
        return;
      }
      
      setAuthUsers(users || []);
      setUserProfiles(users?.map((user: AuthUser) => ({
        id: user.id,
        email: user.email || '',
        full_name: user.full_name,
        role: user.role as UserRole || 'user',
        created_at: user.created_at || new Date().toISOString(),
        updated_at: user.updated_at || new Date().toISOString(),
      })) || []);
    } catch (error) {
      console.error('Error in fetchUsers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoleRequests = async () => {
    try {
      const { data, error } = await supabaseClient
        .from('admin_role_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching role requests:', error);
        return;
      }

      setRoleRequests(data || []);
    } catch (error) {
      console.error('Error in fetchRoleRequests:', error);
    }
  };

  const updateUserRole = async (userId: string, newRole: UserRole) => {
    try {
      setUpdating(userId);
      
      // Check if user profile exists
      const existingProfile = userProfiles.find(p => p.id === userId);
      
      if (existingProfile) {
        // Update existing profile
        const { error } = await supabaseClient
          .from('user_profiles')
          .update({ 
            role: newRole,
            updated_at: new Date().toISOString()
          })
          .eq('id', userId);

        if (error) {
          console.error('Error updating user role:', error);
          return;
        }
      } else {
        // Create new profile
        const authUser = authUsers.find(u => u.id === userId);
        if (!authUser) return;

        const { error } = await supabaseClient
          .from('user_profiles')
          .insert({
            id: userId,
            email: authUser.email,
            full_name: authUser.full_name || '',
            role: newRole,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) {
          console.error('Error creating user profile:', error);
          return;
        }
      }

      // Refresh data
      await fetchUsers();
    } catch (error) {
      console.error('Error in updateUserRole:', error);
    } finally {
      setUpdating(null);
    }
  };

  const approveRoleRequest = async (request: RoleRequest) => {
    try {
      setUpdating(request.user_id);
      await updateUserRole(request.user_id, 'local_govt_admin');
      const { error } = await supabaseClient
        .from('admin_role_requests')
        .update({
          status: 'approved',
          updated_at: new Date().toISOString()
        })
        .eq('id', request.id);

      if (error) {
        console.error('Error updating role request status:', error);
        return;
      }

      await fetchRoleRequests();
    } catch (error) {
      console.error('Error approving role request:', error);
    } finally {
      setUpdating(null);
    }
  };

  const rejectRoleRequest = async (request: RoleRequest) => {
    try {
      setUpdating(request.user_id);
      const { error } = await supabaseClient
        .from('admin_role_requests')
        .update({
          status: 'rejected',
          updated_at: new Date().toISOString()
        })
        .eq('id', request.id);

      if (error) {
        console.error('Error rejecting role request:', error);
        return;
      }

      await fetchRoleRequests();
    } catch (error) {
      console.error('Error rejecting role request:', error);
    } finally {
      setUpdating(null);
    }
  };

  const getRoleForUser = (userId: string): UserRole => {
    const profile = userProfiles.find(p => p.id === userId);
    return profile?.role || 'user';
  };

  const getDisplayName = (user: AuthUser): string => {
    return user.full_name || 
           (user.email ? user.email.split('@')[0] : '') || 
           'Unknown User';
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Access: super_admin and developer have full access; local_govt_admin sees stats only
  if (!(hasRole('super_admin') || hasRole('developer') || hasRole('local_govt_admin'))) {
    return (
      <div className="user-management-page">
        <div className="access-denied">
          <h1>Access Denied</h1>
          <p>You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="user-management-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management-page">
      <div className="user-management-header">
        <h1>User Management</h1>
        <p>Manage user roles and permissions for the Climate Plans platform</p>
      </div>

      <div className="user-management-content">
        {hasRole('super_admin') && roleRequests.length > 0 && (
          <div className="users-table-container">
            <h2>Pending Local Government Admin Requests</h2>
            <table className="users-table">
              <thead>
                <tr>
                  <th>Requester</th>
                  <th>Email</th>
                  <th>Location</th>
                  <th>Requested Role</th>
                  <th>Status</th>
                  <th>Requested At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {roleRequests.map((request) => {
                  const isUpdating = updating === request.user_id;
                  return (
                    <tr key={request.id}>
                      <td className="user-info">
                        <div className="user-details">
                          <div className="user-name">{request.requester_name}</div>
                          <div className="user-id">{request.user_id.substring(0, 8)}...</div>
                        </div>
                      </td>
                      <td className="user-email">{request.requester_email}</td>
                      <td>{request.requested_location}{request.requested_location_type ? `, ${request.requested_location_type}` : ''}</td>
                      <td>{request.requested_role.replace('_', ' ').toUpperCase()}</td>
                      <td>
                        <span className={`role-badge role-${request.status}`}>
                          {request.status.toUpperCase()}
                        </span>
                      </td>
                      <td>{formatDate(request.created_at)}</td>
                      <td className="user-actions">
                        {request.status === 'pending' ? (
                          <div className="role-actions">
                            <button
                              className="role-approve-btn"
                              onClick={() => approveRoleRequest(request)}
                              disabled={isUpdating}
                            >
                              Approve
                            </button>
                            <button
                              className="role-reject-btn"
                              onClick={() => rejectRoleRequest(request)}
                              disabled={isUpdating}
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          <span className="role-note">Reviewed</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {(hasRole('super_admin') || hasRole('developer')) && (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Created</th>
                <th>Last Sign In</th>
                <th>Roles</th>
              </tr>
            </thead>
            <tbody>
              {authUsers.map((user) => {
                const currentRole = getRoleForUser(user.id);
                const isUpdating = updating === user.id;
                
                return (
                  <tr key={user.id}>
                    <td className="user-info">
                      <div className="user-avatar">
                        {getDisplayName(user).charAt(0).toUpperCase()}
                      </div>
                      <div className="user-details">
                        <div className="user-name">{getDisplayName(user)}</div>
                        <div className="user-id">{user.id.substring(0, 8)}...</div>
                      </div>
                    </td>
                    <td className="user-email">{user.email || 'No email'}</td>
                    <td className="user-role">
                      <span className={`role-badge role-${currentRole}`}>
                        {currentRole.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="user-created">
                      {user.created_at ? formatDate(user.created_at) : 'Unknown'}
                    </td>
                    <td className="user-last-signin">
                      {user.last_sign_in_at ? formatDate(user.last_sign_in_at) : 'Never'}
                    </td>
                    <td className="user-actions">
                      {hasRole('super_admin') ? (
                        <select
                          value={currentRole}
                          onChange={(e) => updateUserRole(user.id, e.target.value as UserRole)}
                          disabled={isUpdating}
                          className="role-select"
                        >
                          <option value="user">User</option>
                          <option value="developer">Developer</option>
                          <option value="local_govt_admin_pending">Local Govt Admin (Pending)</option>
                          <option value="local_govt_admin">Local Govt Admin</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                      ) : (
                        <span className="role-display">
                          <span className={`role-badge role-${currentRole}`}>
                            {currentRole.replace('_', ' ').toUpperCase()}
                          </span>
                          <small className="role-note">Only Super Admins can change roles</small>
                        </span>
                      )}
                      {isUpdating && (
                        <div className="updating-indicator">Updating...</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}

        <div className="user-management-stats">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{authUsers.length}</div>
              <div className="stat-label">Total Users</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">
                {userProfiles.filter(p => p.role === 'super_admin').length}
              </div>
              <div className="stat-label">Super Admins</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">
                {userProfiles.filter(p => p.role === 'local_govt_admin').length}
              </div>
              <div className="stat-label">Local Govt Admins</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">
                {userProfiles.filter(p => p.role === 'developer').length}
              </div>
              <div className="stat-label">Developers</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">
                {userProfiles.filter(p => p.role === 'user').length}
              </div>
              <div className="stat-label">Users</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagementPage;
